/* Acceptable Use Policy — incorporated by reference into the Terms.
   This is the IP-protection + abuse-prevention moat: explicitly bans
   reverse engineering, scraping, framing, cloning, and adversarial
   probing. Violations are termination-grade. */
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function AcceptableUse() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-acceptable-use">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Acceptable Use Policy</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            This Acceptable Use Policy ("AUP") governs every use of the verqo
            Platform. Violations may result in immediate suspension or
            termination of your account, forfeiture of fees, and (where
            applicable) civil or criminal action.
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. You will NOT</h2>
          <ul>
            <li><strong>Reverse engineer</strong> the Platform, its frontend, backend, AI models, scoring weights, or any of its data structures.</li>
            <li><strong>Scrape, crawl, frame, embed, or systematically extract</strong> any portion of the Platform's UI, workflows, catalogue, buyer/seller graph, or transactional data.</li>
            <li><strong>Clone</strong> the look, feel, layout, navigation, copy, or interaction patterns of the Platform in any competing product.</li>
            <li>Use the Platform to <strong>train, fine-tune, or evaluate</strong> any AI/ML model, dataset, or "competitive intelligence" product.</li>
            <li>Run any <strong>automated agent, bot, or headless browser</strong> against the Platform without written consent (legitimate API integrations under our published OpenAPI spec are fine).</li>
            <li>Attempt to <strong>probe, scan, or test</strong> the security of the Platform or circumvent any rate limit, authentication, or access control. (Responsible disclosures via <a href="mailto:security@traqo.com" className="underline">security@traqo.com</a> are welcomed.)</li>
            <li>Use the Platform for any activity that violates US, state, or applicable foreign law — including AML, OFAC, export-control, tax-evasion, or fraud.</li>
            <li>Submit <strong>false KYB / KYC information</strong>, false beneficial ownership data, or use the Platform on behalf of an entity you are not authorised to represent.</li>
            <li>Use the Platform to launder money, finance terrorism, or transact in goods prohibited by law (drug paraphernalia, weapons, counterfeit goods, copyrighted material you don't own, etc.).</li>
            <li>Send spam, phishing, harassment, or malware through any verqo communication channel (messaging, support, payment notes).</li>
            <li>Impersonate another business, individual, or verqo employee.</li>
            <li>Disrupt the Platform's operation through denial-of-service, resource exhaustion, or any other means.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">2. Multi-tenant isolation</h2>
          <p>
            The Platform serves many sellers and buyers. You must never attempt
            to access, infer, or correlate data belonging to another tenant
            other than the data we explicitly surface to you (e.g., the buyers
            connected to your tenant). Authorised users only see what their
            role grants them. Any attempt to bypass tenant isolation will
            result in immediate termination and may be reported to law
            enforcement.
          </p>

          <h2 className="display text-xl mt-8 mb-2">3. Fair use of credit &amp; insurance</h2>
          <ul>
            <li>You may not apply for credit or trade insurance on behalf of an entity you don't legally control.</li>
            <li>You may not falsify financial statements, banking data, or business activity to inflate a credit decision.</li>
            <li>You may not "round-trip" goods between connected accounts to inflate apparent volume.</li>
            <li>Discovered fraud is reported to Allianz Trade, our banking partners, and applicable regulators (FinCEN SAR filings as required).</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">4. Reporting violations</h2>
          <p>
            If you observe a violation of this AUP — yours or someone else's —
            email <a href="mailto:abuse@verqo.com" className="underline">abuse@verqo.com</a>.
            For security vulnerabilities, please use <a href="mailto:security@traqo.com" className="underline">security@traqo.com</a> (we welcome
            responsible disclosure and do not pursue legal action against good-faith researchers
            who follow our coordinated-disclosure timeline).
          </p>

          <h2 className="display text-xl mt-8 mb-2">5. Enforcement</h2>
          <p>
            verqo may enforce this AUP at its sole discretion through warnings,
            rate-limiting, feature suspension, account suspension, account
            termination, fee forfeiture, civil action, or referral to law
            enforcement. We are not required to monitor user activity but
            reserve the right to investigate any reasonable suspicion of a
            violation.
          </p>
        </div>
      </div>
    </div>
  );
}
